// src/pages/AttendanceWorkspace.jsx
// 出席記録（全画面）【2026-10-13 作り直し】
//
//   もとは1ページに 全体集計 → 書き出し（プレビュー＋設定）→ 小テスト取り込み → 出席CSV取り込み → 回ごとのサマリ
//   が縦に並んでいて、目的のところまでスクロールが長く、見る・直す・出す・取り込むが混ざっていた。
//   全画面にして、性格の違う作業を分けた。
//     ・上の帯 … 戻る・メニュー・授業の切り替え・期間・平均出席率・「取り込む」「書き出す…」
//     ・タブ   … 概要 / 出欠表 / 回ごと / 小テスト・成績 / 打刻明細・変更履歴（?tab= で URL にも残す）
//     ・書き出す … ダイアログ（WsExportDialog）
//     ・取り込む … 右から出る引き出し。中身はもとの AssessmentImportPane のまま。
//                  画面のどこにファイルを落としても取り込める（落とすと引き出しが開く）。出席記録CSVもここ。
//   サイドバーは Layout が隠し、左端にカーソルを寄せると出る（Layout.jsx）。
//   「すべての授業」のときは、授業ごとの一覧と、書き出しの一覧表だけ。
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useShell } from '../layout/Layout';
import { usePeriod, PeriodSelect } from '../components/PeriodBar';
import Icon from '../components/Icon';
import AssessmentImportPane from '../components/AssessmentImportPane';
import AllClassesSummaryPane from '../components/AllClassesSummaryPane';
import WsOverview from '../components/ws/WsOverview';
import WsMatrix from '../components/ws/WsMatrix';
import WsSessions from '../components/ws/WsSessions';
import WsSheets from '../components/ws/WsSheets';
import WsExportDialog from '../components/ws/WsExportDialog';
import { riskOf, authHeaders } from '../components/ws/wsCommon';

const TABS_ONE = [
  { key: 'overview', label: '概要' },
  { key: 'matrix',   label: '出欠表' },
  { key: 'sessions', label: '回ごと' },
  { key: 'scores',   label: '小テスト・成績' },
  { key: 'log',      label: '打刻明細・変更履歴' },
];
const TABS_ALL = [
  { key: 'overview', label: '授業ごと' },
  { key: 'sheets',   label: '一覧表' },
];
const SCORE_SHEETS = [
  { key: 'grade',      label: '成績集計',   desc: '出席3点/回＋小テスト30点換算＋感想（字数の段階制）＋発言2点/回、100点上限' },
  { key: 'quiz',       label: '小テスト',   desc: '学生×小テストの得点。欠席した日の提出は「不正疑い」' },
  { key: 'reflect',    label: '感想・発言', desc: '学生×回の感想の字数と発言回数' },
  { key: 'submission', label: '提出物',     desc: '学生×課題の ○（期限内）△（締切後）✗（未提出）' },
  { key: 'fraud',      label: '不正疑い',   desc: '欠席した日に小テストを提出している記録' },
];
const LOG_SHEETS = [
  { key: 'detail',       label: '打刻明細',         desc: '1行1記録。時刻・打刻方法・信頼度つき' },
  { key: 'audit',        label: '変更履歴',         desc: '出席記録を手で直した証跡' },
  { key: 'unregistered', label: 'アカウント未作成', desc: '名簿にはいるが、まだアプリに登録していない学生' },
  { key: 'trend',        label: '回ごとの推移',     desc: '各授業日の出席・遅刻・早退・欠席の人数と授業メモ' },
];
const ALL_SHEETS = [
  { key: 'register', label: '授業別サマリ', desc: '授業を1行ずつ並べた一覧（書き出しの1枚目）' },
  { key: 'risk',     label: '要注意の学生', desc: '授業名の列を足して1枚にまとめたもの' },
  { key: 'trend',    label: '回ごとの推移' },
  { key: 'detail',   label: '打刻明細' },
  { key: 'audit',    label: '変更履歴' },
];

export default function AttendanceWorkspace({ classId }) {
  const navigate = useNavigate();
  const { openNav } = useShell();
  const isAll = String(classId) === 'all';
  const [params, setParams] = useSearchParams();
  const tabs = isAll ? TABS_ALL : TABS_ONE;
  const tab = tabs.some(t => t.key === params.get('tab')) ? params.get('tab') : 'overview';
  const [focus, setFocus] = useState(null);     // タブを移るときに渡すもの（回・学生・絞り込み）

  const goTab = useCallback((key, f) => {
    setFocus(f ? { ...f, n: Date.now() } : null);
    const next = new URLSearchParams(params);
    if (key === 'overview') next.delete('tab'); else next.set('tab', key);
    setParams(next, { replace: true });
  }, [params, setParams]);

  // ---- 授業の切り替え（今の期間の授業だけ） ----
  const period = usePeriod();
  const [allClasses, setAllClasses] = useState([]);
  const classes = useMemo(() => period.filter(allClasses), [period.filter, allClasses]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!period.ready) return;
    const q = period.year ? '?academicYear=' + period.year : '';
    fetch('/api/schedule/classes/overview' + q, { headers: authHeaders() })
      .then(r => r.json()).then(d => setAllClasses(d.classes || [])).catch(() => {});
  }, [period.ready, period.year]);

  // ---- データ ----
  const [summary, setSummary] = useState(null);
  const [matrix, setMatrix]   = useState(null);
  const [todo, setTodo]       = useState({ requests: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const loadSummary = useCallback(async () => {
    if (isAll) return;
    try {
      const res = await fetch('/api/class-summary/' + classId, { headers: authHeaders() });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || d.message || ('HTTP ' + res.status));
      setSummary(d);
    } catch (e) { setErr(e.message); setSummary(null); }
  }, [classId, isAll]);
  const loadMatrix = useCallback(async () => {
    if (isAll) return;
    try {
      const r = await fetch('/api/schedule/classes/' + classId + '/attendance-matrix', { headers: authHeaders() });
      const d = await r.json();
      setMatrix(r.ok && d.success ? d : { sessions: [], students: [] });
    } catch (e) { setMatrix({ sessions: [], students: [] }); }
  }, [classId, isAll]);
  const loadTodo = useCallback(async () => {
    if (isAll) return;
    const h = { headers: authHeaders() };
    const [a, b] = await Promise.all([
      fetch('/api/attendance/requests/inbox?status=open&classId=' + encodeURIComponent(classId), h).then(r => r.json()).catch(() => ({})),
      fetch('/api/attendance/pending?classId=' + encodeURIComponent(classId), h).then(r => r.json()).catch(() => ({})),
    ]);
    setTodo({ requests: ((a && a.requests) || []).length, pending: ((b && b.items) || []).length });
  }, [classId, isAll]);
  const reloadAll = useCallback(() => { loadSummary(); loadMatrix(); loadTodo(); }, [loadSummary, loadMatrix, loadTodo]);

  useEffect(() => {
    setSummary(null); setMatrix(null); setErr(''); setMsg(''); setFocus(null);
    if (isAll) { setLoading(false); return; }
    setLoading(true);
    Promise.all([loadSummary(), loadMatrix(), loadTodo()]).finally(() => setLoading(false));
    try { localStorage.setItem('lastClassId', String(classId)); } catch (e) { /* 保存できなくても動く */ }
  }, [classId]); // eslint-disable-line react-hooks/exhaustive-deps

  const risk = useMemo(() => riskOf(matrix), [matrix]);

  // ---- 取り込み・書き出し・メニュー ----
  const [importOpen, setImportOpen] = useState(false);
  const [importCount, setImportCount] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [menuOpen]);
  useEffect(() => {
    if (!importOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setImportOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [importOpen]);

  // メッセージは少ししたら消す（エラーは消さない）
  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(() => setMsg(''), 7000);
    return () => clearTimeout(t);
  }, [msg]);

  const cls = (matrix && matrix.class) || (summary && summary.class) || null;
  const className = (cls && cls.name) || (classes.find(c => String(c.id) === String(classId)) || {}).name || '';
  const readOnly = !!(cls && cls.closure_id);
  const ov = (summary && summary.overview) || null;
  const enrolled = ov ? Number(ov.enrolled_students) : 0;
  const held = ov ? Number(ov.total_sessions) - Number(ov.cancelled_sessions) : 0;
  const rate = ov && ov.average_attendance_rate != null ? Number(ov.average_attendance_rate) : null;
  const marks = ov ? Number(ov.present_total) + Number(ov.late_total) + Number(ov.early_leave_total) : 0;

  /** 出席した学生がいない授業は消せる（もとの出席記録と同じ2段階の確認） */
  const deleteThisClass = async () => {
    setMenuOpen(false);
    const name = className || 'この授業';
    const send = (confirmRecords) => fetch('/api/schedule/classes/' + classId, {
      method: 'DELETE', headers: authHeaders(),
      ...(confirmRecords === undefined ? {} : { body: JSON.stringify({ confirmRecords }) }),
    });
    try {
      const r1 = await send();
      const d1 = await r1.json();
      let done = d1;
      if (d1.code === 'CONFIRM_REQUIRED') {
        const warn = d1.count > 0
          ? `「${name}」を削除します。\n\n欠席の記録 ${d1.count} 件も一緒に消えます。\n元には戻せません。\n\n本当に削除しますか？`
          : `「${name}」を削除します。\n元には戻せません。\n\n本当に削除しますか？`;
        if (!window.confirm(warn)) return;
        const r2 = await send(d1.count);
        done = await r2.json();
        if (!r2.ok && !done.success) throw new Error(done.message || '削除に失敗しました');
      } else if (!r1.ok && !d1.success) {
        throw new Error(d1.message || '削除に失敗しました');
      }
      try { if (localStorage.getItem('lastClassId') === String(classId)) localStorage.removeItem('lastClassId'); } catch (e) { /* 動く */ }
      navigate('/classes');
    } catch (e) { setErr(e.message); }
  };

  const switchClass = (v) => {
    const keep = (v === 'all' ? TABS_ALL : TABS_ONE).some(t => t.key === tab) && tab !== 'overview' ? '?tab=' + tab : '';
    navigate('/classes/' + v + '/summary' + keep);
  };

  const common = { classId, auth: authHeaders, setErr, setMsg };

  return (
    <div className="ws-root">
      {/* ── 上の帯 ── */}
      <header className="ws-header">
        <button type="button" className="ws-hbtn ws-hbtn-icon" onClick={openNav}
                aria-label="メニューを開く" title="メニュー（画面の左端にカーソルを寄せても開きます）">
          <Icon name="menu" size={20} />
        </button>
        <Link to="/classes" className="ws-back">← 授業管理</Link>
        <label className="ws-hfield">
          <span>授業</span>
          <select value={classId} onChange={e => switchClass(e.target.value)} className="ws-class-select">
            <option value="all">— すべての授業 —</option>
            {!isAll && cls && !classes.some(c => String(c.id) === String(classId)) && (
              <option value={classId}>{cls.name}（ほかの期間）</option>
            )}
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}{c.semester ? '（' + c.semester + '）' : ''}</option>)}
          </select>
        </label>
        {period.ready && period.available && (
          <div className="ws-hfield">
            <span>期間</span>
            <PeriodSelect period={period} className={'ws-period-select' + (period.isCurrent ? '' : ' is-other')} />
            {!period.isCurrent && (
              <button type="button" className="ws-hbtn" onClick={() => period.reset()} title="今の期間の授業に戻す">今に戻る</button>
            )}
          </div>
        )}
        <span className="ws-hgap" />
        {!isAll && ov && (
          <div className="ws-kpis">
            <span>平均出席率 <b className="ws-kpi-big">{rate == null ? '—' : rate}</b>{rate != null && '%'}</span>
            <span>履修 <b>{enrolled}</b>人</span>
            <span>実施 <b>{held}</b>回</span>
          </div>
        )}
        {!isAll && (
          <button type="button" className="ws-hbtn" onClick={() => setImportOpen(true)} disabled={readOnly}
                  title={readOnly ? '年度の締めが済んだ授業には取り込めません' : 'manaba の小テスト・課題、出席記録CSV（画面のどこに落としても取り込めます）'}>
            取り込む{importCount > 0 ? `（${importCount}）` : ''}
          </button>
        )}
        <button type="button" className="ws-hbtn ws-hbtn-primary" onClick={() => setExportOpen(true)}>書き出す…</button>
      </header>

      {/* ── タブ ── */}
      <nav className="ws-tabs" aria-label="表示の切り替え">
        {tabs.map(t => (
          <button key={t.key} type="button" className={'ws-tab' + (tab === t.key ? ' on' : '')}
                  aria-current={tab === t.key ? 'page' : undefined} onClick={() => goTab(t.key)}>
            {t.label}
            {t.key === 'matrix' && risk.size > 0 && <span className="ws-tab-badge" title="要注意の学生">{risk.size}</span>}
          </button>
        ))}
        <span className="ws-hgap" />
        {!isAll && (
          <button type="button" className="ws-tab-tool" onClick={reloadAll} title="読み直す">↻ 読み直す</button>
        )}
        {!isAll && (
          <div className="ws-menu" ref={menuRef}>
            <button type="button" className="ws-tab-tool" aria-label="その他の操作" aria-expanded={menuOpen}
                    onClick={() => setMenuOpen(o => !o)}>…</button>
            {menuOpen && (
              <div className="ws-pop ws-menu-pop" role="menu">
                <Link role="menuitem" to={'/classes/' + classId + '/edit'}>出欠の確認・修正（まとめて直す）</Link>
                <Link role="menuitem" to={'/classes/' + classId + '/merge'}>回をまとめる…</Link>
                <Link role="menuitem" to={'/classes/' + classId + '/schedule'}>授業スケジュール</Link>
                <Link role="menuitem" to={'/students?classId=' + classId}>学生管理で名簿を開く</Link>
                <hr />
                <button type="button" role="menuitem" className="is-danger" disabled={marks > 0 || readOnly} onClick={deleteThisClass}
                        title={marks > 0 ? '出席した学生がいる授業は、ここからは消せません（授業管理から畳めます）' : ''}>
                  この授業を削除…
                </button>
                {marks > 0 && <div className="ws-muted ws-menu-note">出席した学生がいる授業は、ここからは消せません</div>}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* ── お知らせ ── */}
      {!isAll && cls && cls.linked_to_class_id && (
        <div className="ws-banner">
          この授業は「{cls.linked_to_name}」にまとめています。履修者・出席・集計はまとめ先にあります。
          <Link to={'/classes/' + cls.linked_to_class_id + '/summary'}>まとめ先の出席記録を開く →</Link>
        </div>
      )}
      {!isAll && matrix && (matrix.linkedChildren || []).length > 0 && (
        <div className="ws-banner">
          別学部の同じ授業 {matrix.linkedChildren.map(k => '「' + k.name + '」').join('')} をこの授業にまとめて集計しています。
        </div>
      )}
      {readOnly && <div className="ws-banner">この授業は年度の締めが済んでいるため、名簿・出席は見るだけです。</div>}

      {/* ── 本体 ── */}
      <div className="ws-body">
        {isAll ? (
          tab === 'sheets'
            ? <WsSheets classId="all" isAll periodQuery={period.query} sheets={ALL_SHEETS} setErr={setErr} />
            : <div className="ws-allpane"><AllClassesSummaryPane setErr={setErr} periodQuery={period.query} /></div>
        ) : loading ? (
          <div className="ws-panel"><p className="ws-muted">読み込み中…</p></div>
        ) : tab === 'matrix' ? (
          <WsMatrix {...common} matrix={matrix} risk={risk} readOnly={readOnly} focus={focus}
                    onChanged={() => { loadMatrix(); loadSummary(); loadTodo(); }} />
        ) : tab === 'sessions' ? (
          <WsSessions {...common} data={summary} matrix={matrix} focus={focus} readOnly={readOnly}
                      onChanged={() => { loadSummary(); loadMatrix(); }} />
        ) : tab === 'scores' ? (
          <WsSheets classId={classId} sheets={SCORE_SHEETS} setErr={setErr}
                    emptyHint="manaba の小テスト・課題・感想のファイルを、この画面のどこかに落とすと取り込めます（右上の「取り込む」からも）。" />
        ) : tab === 'log' ? (
          <WsSheets classId={classId} sheets={LOG_SHEETS} setErr={setErr} />
        ) : (
          <WsOverview {...common} data={summary} matrix={matrix} risk={risk} todo={todo} goTab={goTab}
                      onChanged={reloadAll} />
        )}
      </div>

      {/* ── 取り込む（右から出る引き出し）──
           中身はいつも置いておく（閉じていても、画面のどこに落としても受け取れるように）。 */}
      {!isAll && !readOnly && (
        <>
          {importOpen && <div className="ws-drawer-back" onClick={() => setImportOpen(false)} />}
          <section className={'ws-drawer' + (importOpen ? ' open' : '')} aria-hidden={!importOpen}
                   role="dialog" aria-label="取り込む">
            <div className="ws-drawer-head">
              <h2>取り込む</h2>
              <span className="ws-muted" style={{ fontSize: '0.82rem' }}>
                ファイルやフォルダは、この画面のどこに落としても取り込めます（出席記録CSVも）
              </span>
              <button type="button" className="ws-icon-btn" aria-label="閉じる" onClick={() => setImportOpen(false)}>×</button>
            </div>
            <div className="ws-drawer-body">
              <AssessmentImportPane classId={classId} className={className} setErr={setErr} setMsg={setMsg}
                                    onAccept={(n) => { setImportOpen(true); setImportCount(c => c + n); }}
                                    onImported={() => { setImportCount(0); reloadAll(); }} />
            </div>
          </section>
        </>
      )}

      <WsExportDialog open={exportOpen} onClose={() => setExportOpen(false)}
                      classId={classId} isAll={isAll} className={isAll ? '全授業' : className}
                      periodQuery={period.query} setErr={setErr} setMsg={setMsg} />

      {/* ── お知らせ（右下）── */}
      <div className="ws-toasts" aria-live="polite">
        {err && (
          <div className="ws-toast ws-toast-err" role="alert">
            <span>{err}</span>
            <button type="button" aria-label="閉じる" onClick={() => setErr('')}>×</button>
          </div>
        )}
        {msg && (
          <div className="ws-toast ws-toast-ok">
            <span>{msg}</span>
            <button type="button" aria-label="閉じる" onClick={() => setMsg('')}>×</button>
          </div>
        )}
      </div>
    </div>
  );
}
